import { queryOptions } from "@tanstack/react-query";
import type { CheckoutSchemas } from "api-types";
import { axiosClient } from "auth-sdk";

const CHECKOUT_QUOTE_ENDPOINT = "/api/:customerSlug/checkout/api/quote";
const CHECKOUT_CALL_STATE_ENDPOINT = "/api/:customerSlug/checkout/api/call-state";

export type CheckoutQuote = CheckoutSchemas.CheckoutQuoteResponse;
export type CheckoutCallState = CheckoutSchemas.CheckoutCallStateResponse;

export const CHECKOUT_QUOTE_QUERY_KEY = ["checkout-home", "quote"] as const;
export const getCheckoutCallStateQueryKey = (customerDomain: string) =>
  ["checkout", customerDomain, "call-state"] as const;

/** A 204 means the merchant has no quote and the page must not render. */
const getCheckoutQuote = async (customerDomain: string): Promise<CheckoutQuote | null> => {
  const url = CHECKOUT_QUOTE_ENDPOINT.replace(":customerSlug", customerDomain);
  const response = await axiosClient.get<CheckoutQuote>(url);
  if (response.status === 204) {
    return null;
  }
  return response.data;
};

export const getCheckoutQuoteQueryOptions = (customerDomain: string) =>
  queryOptions({
    queryKey: [...CHECKOUT_QUOTE_QUERY_KEY, customerDomain],
    queryFn: () => getCheckoutQuote(customerDomain),
    enabled: !!customerDomain,
    // The quote gates the page, so fail fast instead of holding an empty screen through retries.
    retry: false,
    // Quote changes come from staff imports, not in-session interaction.
    staleTime: 1000 * 60 * 5,
  });

const getCheckoutCallState = async (customerDomain: string): Promise<CheckoutCallState> => {
  const url = CHECKOUT_CALL_STATE_ENDPOINT.replace(":customerSlug", customerDomain);
  const response = await axiosClient.get<CheckoutCallState>(url);
  return response.data;
};

/** Company-level state: a colleague's request in another session must show up on revisit. */
export const getCheckoutCallStateQueryOptions = (customerDomain: string) =>
  queryOptions({
    queryKey: getCheckoutCallStateQueryKey(customerDomain),
    queryFn: () => getCheckoutCallState(customerDomain),
    enabled: !!customerDomain,
    retry: false,
    refetchOnWindowFocus: true,
  });
